import { useEffect, useRef, useState } from 'react';
import { usePageParam } from '../../hooks/usePageParam';
import { useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Pagination from '../../components/common/Pagination';
import ActionsMenu from '../../components/common/ActionsMenu';
import { RELEASE_NOTES_QUERY, RELEASE_NOTE_QUERY } from '../../graphql/queries';
import {
  CREATE_RELEASE_NOTE_MUTATION,
  UPDATE_RELEASE_NOTE_MUTATION,
  DELETE_RELEASE_NOTE_MUTATION,
  GENERATE_RELEASE_NOTE_SHARE_LINK_MUTATION,
  REVOKE_RELEASE_NOTE_SHARE_LINK_MUTATION,
} from '../../graphql/mutations';
import { putWithProgress } from '../../utils/putWithProgress';
import styles from './AdminReleaseNotes.module.css';
import Icon from '../../components/common/Icon';

interface DraftFile {
  s3Key: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}

interface DraftItem {
  uid: string;
  title: string;
  description: string;
  files: DraftFile[];
}

interface ReleaseNoteRow {
  id: string;
  version: string;
  releaseDate: string;
  shareToken: string | null;
  items: { title: string }[];
}

const newItem = (): DraftItem => ({ uid: crypto.randomUUID(), title: '', description: '', files: [] });

const todayInput = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Direct browser → S3 PUT with progress; the caller falls back to the backend
// stream proxy when the bucket CORS rejects this origin (localhost dev).
const AdminReleaseNotes = () => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const [version, setVersion] = useState('');
  const [releaseDate, setReleaseDate] = useState(todayInput());
  const [items, setItems] = useState<DraftItem[]>([newItem()]);
  // Editing an existing note: its id, plus the version at load time for the banner.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingVersion, setEditingVersion] = useState('');
  // Keyed by a per-upload uuid — a filename key would collide when the same
  // name uploads twice and unblock the publish button while one is in flight.
  const [uploads, setUploads] = useState<Record<string, { pct: number; name: string; uid: string }>>({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Drag-reorder of items (started from the per-item drag handle).
  const [dragUid, setDragUid] = useState<string | null>(null);
  const [dropMark, setDropMark] = useState<{ uid: string; before: boolean } | null>(null);

  const [page, setPage] = usePageParam();
  const { data, loading: listLoading, error: listError, refetch } = useQuery(RELEASE_NOTES_QUERY, {
    variables: { page, limit: 30 },
    fetchPolicy: 'cache-and-network',
  });
  const notes: ReleaseNoteRow[] = data?.releaseNotes?.notes ?? [];
  const total: number = data?.releaseNotes?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 30));

  // Only with this page's own data: while a page loads, data is undefined and total reads 0.
  useEffect(() => { if (data && page > totalPages) setPage(totalPages); }, [data, page, totalPages]);

  // s3Keys belonging to the note currently loaded for editing — protected on
  // the server anyway (referenced keys 409 on delete), tracked so draft
  // cleanup only targets this session's uploads.
  const loadedKeysRef = useRef<Set<string>>(new Set());

  // Fire-and-forget delete for every draft file that isn't part of a saved
  // note. Safe to call on successful publish too: by then the keys are
  // referenced and the server refuses to delete them.
  const cleanupDraftUploads = () => {
    itemsRef.current.forEach((it) => it.files.forEach((f) => {
      if (!loadedKeysRef.current.has(f.s3Key)) deleteUploadedObject(f.s3Key);
    }));
  };

  const resetForm = () => {
    setVersion('');
    setReleaseDate(todayInput());
    setItems([newItem()]);
    setEditingId(null);
    setEditingVersion('');
    loadedKeysRef.current = new Set();
  };

  const cancelForm = () => {
    cleanupDraftUploads();
    resetForm();
  };

  const flashSuccess = (msg: string) => {
    setSuccess(msg);
    setError('');
    window.setTimeout(() => setSuccess(''), 5000);
  };

  const [createReleaseNote, { loading: creating }] = useMutation(CREATE_RELEASE_NOTE_MUTATION, {
    onCompleted: (d) => {
      flashSuccess(`Release note ${d.createReleaseNote.version} published`);
      resetForm();
      refetch();
    },
    onError: (e) => setError(e.message),
  });

  const [updateReleaseNote, { loading: updating }] = useMutation(UPDATE_RELEASE_NOTE_MUTATION, {
    onCompleted: (d) => {
      flashSuccess(`Release note ${d.updateReleaseNote.version} updated`);
      resetForm();
      refetch();
    },
    onError: (e) => setError(e.message),
  });

  const [deleteReleaseNote] = useMutation(DELETE_RELEASE_NOTE_MUTATION, {
    onCompleted: () => refetch(),
    onError: (e) => setError(e.message),
  });

  const shareUrl = (token: string) => `${window.location.origin}/share/release-notes/${token}`;

  const copyShareLink = async (token: string) => {
    const url = shareUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      flashSuccess('Share link copied to clipboard');
    } catch {
      // Clipboard can be blocked (permissions, non-secure context) — show the
      // link so the admin can copy it manually.
      flashSuccess(`Share link: ${url}`);
    }
  };

  const [generateShareLink] = useMutation(GENERATE_RELEASE_NOTE_SHARE_LINK_MUTATION, {
    onCompleted: (d) => {
      refetch();
      if (d.generateReleaseNoteShareLink.shareToken) copyShareLink(d.generateReleaseNoteShareLink.shareToken);
    },
    onError: (e) => setError(e.message),
  });

  const [revokeShareLink] = useMutation(REVOKE_RELEASE_NOTE_SHARE_LINK_MUTATION, {
    onCompleted: () => {
      refetch();
      flashSuccess('Share link revoked');
    },
    onError: (e) => setError(e.message),
  });

  const saving = creating || updating;

  const patchItem = (uid: string, patch: Partial<DraftItem>) =>
    setItems((prev) => prev.map((it) => (it.uid === uid ? { ...it, ...patch } : it)));

  const deleteUploadedObject = (s3Key: string) => {
    // The backend refuses (409) keys still referenced by a published note, so
    // firing this for a published file during an edit is a harmless no-op —
    // updateReleaseNote cleans those up on save instead.
    const token = localStorage.getItem('geodraft_token') || '';
    fetch(`/api/release-notes/upload?key=${encodeURIComponent(s3Key)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  };

  const startEdit = async (row: ReleaseNoteRow) => {
    setError('');
    try {
      const res = await client.query({
        query: RELEASE_NOTE_QUERY,
        variables: { version: row.version },
        fetchPolicy: 'network-only',
      });
      const note = res.data?.releaseNote;
      if (!note) throw new Error('Release note not found');
      // The draft being replaced may hold this-session uploads — orphans if
      // silently clobbered.
      cleanupDraftUploads();
      loadedKeysRef.current = new Set(
        note.items.flatMap((it: { files: { s3Key: string }[] }) => it.files.map((f) => f.s3Key)),
      );
      setEditingId(row.id);
      setEditingVersion(note.version);
      setVersion(note.version);
      setReleaseDate(new Date(note.releaseDate).toISOString().slice(0, 10));
      setItems(note.items.map((it: { title: string; description: string; files: (DraftFile & { url?: string })[] }) => ({
        uid: crypto.randomUUID(),
        title: it.title,
        description: it.description,
        // Strip __typename/url — the mutation input takes only the stored fields.
        files: it.files.map((f) => ({
          s3Key: f.s3Key, fileName: f.fileName, mimeType: f.mimeType, fileSize: f.fileSize,
        })),
      })));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const uploadFiles = async (item: DraftItem, fileList: FileList) => {
    setError('');
    const token = localStorage.getItem('geodraft_token') || '';
    for (const file of Array.from(fileList)) {
      const progressKey = crypto.randomUUID();
      setUploads((u) => ({ ...u, [progressKey]: { pct: 0, name: file.name, uid: item.uid } }));
      try {
        const presignRes = await fetch('/api/release-notes/presign', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ fileName: file.name, mimeType: file.type || 'application/octet-stream' }),
        });
        if (!presignRes.ok) throw new Error('Could not prepare the upload');
        const { s3Key, uploadUrl } = (await presignRes.json()) as { s3Key: string; uploadUrl: string };
        try {
          await putWithProgress(uploadUrl, file, (pct) =>
            setUploads((u) => (u[progressKey] ? { ...u, [progressKey]: { ...u[progressKey], pct } } : u)));
        } catch {
          const proxyRes = await fetch(`/api/release-notes/upload?key=${encodeURIComponent(s3Key)}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type || 'application/octet-stream' },
            body: file,
          });
          if (!proxyRes.ok) throw new Error(`Upload of ${file.name} failed`);
        }
        if (!itemsRef.current.some((it) => it.uid === item.uid)) {
          // Item vanished mid-upload (removal is disabled while uploading, but
          // belt and braces) — don't strand the object in S3.
          deleteUploadedObject(s3Key);
          throw new Error(`The item was removed while "${file.name}" was uploading — the file was discarded`);
        }
        const meta: DraftFile = {
          s3Key,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          fileSize: file.size,
        };
        setItems((prev) => prev.map((it) => (it.uid === item.uid ? { ...it, files: [...it.files, meta] } : it)));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setUploads((u) => {
          const next = { ...u };
          delete next[progressKey];
          return next;
        });
      }
    }
  };

  const reorderOnDrop = () => {
    if (!dragUid || !dropMark || dragUid === dropMark.uid) return;
    setItems((prev) => {
      const from = prev.findIndex((i) => i.uid === dragUid);
      const targetIdx = prev.findIndex((i) => i.uid === dropMark.uid);
      if (from < 0 || targetIdx < 0) return prev;
      let to = targetIdx + (dropMark.before ? 0 : 1);
      if (from < to) to -= 1;
      if (from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const uploading = Object.keys(uploads).length > 0;
  const canSubmit = version.trim() && items.length > 0 && items.every((i) => i.title.trim()) && !uploading && !saving;

  const submit = () => {
    if (!canSubmit) return;
    const variables = {
      version: version.trim(),
      releaseDate: releaseDate || todayInput(),
      items: items.map(({ title, description, files }) => ({ title: title.trim(), description, files })),
    };
    if (editingId) updateReleaseNote({ variables: { id: editingId, ...variables } });
    else createReleaseNote({ variables });
  };

  return (
    <DashboardLayout topBarTitle="Release Notes">
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Release Notes</h2>
          <p className="text-on-surface-variant mt-1">Publish what's new — visible to every user via the megaphone icon.</p>
        </div>

        {error && <div className={styles.errorBanner}>{error}</div>}
        {success && <div className={styles.successBanner}>{success}</div>}

        <div className={styles.formCard}>
          {editingId && (
            <div className={styles.editingBanner}>
              <Icon name="edit" className="text-base" />
              Editing release note {editingVersion}
              <button className={styles.cancelEditBtn} onClick={cancelForm}>Cancel edit</button>
            </div>
          )}

          <div className={styles.headFields}>
            <div>
              <label className={styles.fieldLabel}>Version</label>
              <input
                className={styles.versionInput}
                placeholder="e.g. 230"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
            </div>
            <div>
              <label className={styles.fieldLabel}>Release date</label>
              <input
                type="date"
                className={styles.versionInput}
                value={releaseDate}
                onChange={(e) => setReleaseDate(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.itemsHead}>
            <label className={styles.fieldLabel}>Items</label>
            <button className={styles.addItemBtn} onClick={() => setItems((prev) => [...prev, newItem()])}>
              <Icon name="add" className="text-base" />
              Add item
            </button>
          </div>

          {/* Container-level handlers: the 12px gaps between cards are valid
              drop space — without these, releasing there shows a committed-
              looking insertion line but silently cancels the drop. */}
          <div
            onDragOver={(e) => { if (dragUid) e.preventDefault(); }}
            onDrop={(e) => {
              if (!dragUid) return;
              e.preventDefault();
              reorderOnDrop();
              setDragUid(null);
              setDropMark(null);
            }}
          >
          {items.map((item, idx) => {
            const itemUploading = Object.values(uploads).some((u) => u.uid === item.uid);
            const markClass = dropMark?.uid === item.uid
              ? (dropMark.before ? styles.itemCardDropBefore : styles.itemCardDropAfter)
              : '';
            return (
            <div
              key={item.uid}
              className={`${styles.itemCard} ${dragUid === item.uid ? styles.itemCardDragging : ''} ${markClass}`}
              onDragOver={(e) => {
                if (!dragUid || dragUid === item.uid) return;
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                setDropMark({ uid: item.uid, before: e.clientY < rect.top + rect.height / 2 });
              }}
              /* Drops (on cards AND in the gaps) are handled once by the
                 container — a card-level onDrop would run again on bubble. */
            >
              <div className={styles.itemCardHead}>
                <span
                  className={styles.dragHandle}
                  title="Drag to reorder"
                  draggable
                  onDragStart={(e) => {
                    setDragUid(item.uid);
                    e.dataTransfer.effectAllowed = 'move';
                    // Drag image = the whole card, not just the handle glyph.
                    const card = (e.currentTarget as HTMLElement).closest(`.${styles.itemCard}`);
                    if (card) e.dataTransfer.setDragImage(card, 20, 20);
                  }}
                  onDragEnd={() => { setDragUid(null); setDropMark(null); }}
                >
                  <Icon name="drag_indicator" className="text-lg" />
                </span>
                <span className={styles.itemIndex}>{String(idx + 1).padStart(2, '0')}</span>
                <input
                  className={styles.titleInput}
                  placeholder="Title"
                  value={item.title}
                  onChange={(e) => patchItem(item.uid, { title: e.target.value })}
                />
                <button
                  className={styles.removeItemBtn}
                  onClick={() => {
                    item.files.forEach((f) => deleteUploadedObject(f.s3Key));
                    setItems((prev) => prev.filter((it) => it.uid !== item.uid));
                  }}
                  disabled={items.length === 1 || itemUploading}
                  title={itemUploading
                    ? 'Wait for the uploads to finish'
                    : items.length === 1 ? 'A release note needs at least one item' : 'Remove item'}
                >
                  <Icon name="delete" className="text-lg" />
                </button>
              </div>
              <textarea
                className={styles.descriptionInput}
                placeholder="Description"
                rows={3}
                value={item.description}
                onChange={(e) => patchItem(item.uid, { description: e.target.value })}
              />
              <div className={styles.filesRow}>
                {item.files.map((f) => (
                  <span key={f.s3Key} className={styles.fileChip} title={f.fileName}>
                    <Icon name={f.mimeType.startsWith('image/') ? 'image' : f.mimeType.startsWith('video/') ? 'movie' : 'attach_file'} className="text-base" />
                    {f.fileName}
                    <span className={styles.fileSize}>{formatSize(f.fileSize)}</span>
                    <button
                      className={styles.fileRemove}
                      onClick={() => {
                        deleteUploadedObject(f.s3Key);
                        patchItem(item.uid, { files: item.files.filter((x) => x.s3Key !== f.s3Key) });
                      }}
                      title="Remove file"
                    >
                      <Icon name="close" className="text-sm" />
                    </button>
                  </span>
                ))}
                {Object.entries(uploads)
                  .filter(([, u]) => u.uid === item.uid)
                  .map(([k, u]) => (
                    <span key={k} className={`${styles.fileChip} ${styles.fileChipUploading}`}>
                      <Icon name="progress_activity" className="animate-spin text-base" />
                      {u.name} — {u.pct}%
                    </span>
                  ))}
                <label className={styles.uploadBtn}>
                  <Icon name="upload" className="text-base" />
                  Add files
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files?.length) uploadFiles(item, e.target.files);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>
            );
          })}
          </div>

          <div className={styles.submitRow}>
            {editingId && (
              <button className={styles.cancelBtn} onClick={cancelForm}>Cancel</button>
            )}
            <button className={styles.publishBtn} onClick={submit} disabled={!canSubmit}>
              <Icon name={editingId ? 'save' : 'campaign'} className="text-base" />
              {saving
                ? (editingId ? 'Saving…' : 'Publishing…')
                : uploading
                  ? 'Uploading files…'
                  : editingId ? 'Save changes' : 'Publish release note'}
            </button>
          </div>
        </div>

        <div className={styles.listCard}>
          <h3 className={styles.listTitle}>Published{data ? ` (${total})` : ''}</h3>
          {!data && listError ? (
            <div className={styles.errorBanner}>Could not load the release notes: {listError.message}</div>
          ) : !data && listLoading ? (
            <p className="text-on-surface-variant text-sm py-4">Loading…</p>
          ) : notes.length === 0 ? (
            <p className="text-on-surface-variant text-sm py-4">Nothing published yet.</p>
          ) : (
            notes.map((n) => (
              <div key={n.id} className={styles.listRow}>
                <span className={styles.listVersion}>Version {n.version}</span>
                <span className={styles.listMeta}>
                  {n.items.length} item{n.items.length !== 1 ? 's' : ''} · {formatDate(n.releaseDate)}
                </span>
                {n.shareToken && <span className={styles.sharedChip}>Public link</span>}
                <ActionsMenu
                  label={`Actions for version ${n.version}`}
                  actions={[
                    { label: 'Open', icon: 'open_in_new', onSelect: () => navigate(`/release-notes/${encodeURIComponent(n.version)}`) },
                    { label: 'Edit', icon: 'edit', onSelect: () => startEdit(n) },
                    n.shareToken
                      ? { label: 'Copy public share link', icon: 'link', onSelect: () => copyShareLink(n.shareToken!) }
                      : {
                        label: 'Create public share link', icon: 'link', hint: 'No login needed to open it',
                        onSelect: () => generateShareLink({ variables: { id: n.id } }),
                      },
                    !!n.shareToken && {
                      label: 'Revoke share link', icon: 'link_off', danger: true,
                      onSelect: () => {
                        if (window.confirm(`Revoke the share link for ${n.version}? Anyone with the old link loses access.`)) {
                          revokeShareLink({ variables: { id: n.id } });
                        }
                      },
                    },
                    {
                      label: 'Delete', icon: 'delete', danger: true,
                      onSelect: () => {
                        if (window.confirm(`Delete release note ${n.version}? Its uploaded files are removed too.`)) {
                          if (editingId === n.id) cancelForm();
                          deleteReleaseNote({ variables: { id: n.id } });
                        }
                      },
                    },
                  ]}
                />
              </div>
            ))
          )}
          <Pagination page={page} pageSize={30} total={total} onPageChange={setPage} noun="release notes" variant="inline" />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminReleaseNotes;
